import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiLoader, FiXCircle, FiArrowLeft } from 'react-icons/fi';

import { ProcessedContent } from '../meeting/ProcessedMeetingContent';
const modalRoot = document.getElementById('modal-root');

// Helper to format the meeting date and time
const formatTranscriptDateTime = (eventDate, meetingTime) => {
  const date = new Date(eventDate);
  const time = new Date(meetingTime);

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
  ).toLocaleString(undefined, {
    dateStyle: 'full',
    timeStyle: 'short',
  });
};

const LoadingState = ({ text }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center text-slate-400">
    <FiLoader className="mb-4 h-8 w-8 animate-spin text-blue-400" />
    <p className="font-semibold">{text}</p>
    <p className="text-xs text-slate-500">This may take a moment...</p>
  </div>
);

const FailedState = () => (
  <div className="flex flex-col items-center justify-center p-8 text-center text-red-400">
    <FiXCircle className="mb-4 h-8 w-8" />
    <p className="font-semibold">Processing Failed</p>
    <p className="text-xs text-slate-500">
      There was an error while processing this event.
    </p>
  </div>
);

const MeetingEventDetailModal = ({
  isOpen,
  onClose,
  meeting,
  meetingEvent,
  accessToken,
}) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => (document.body.style.overflow = 'unset');
  }, [isOpen]);

  if (!isOpen || !meetingEvent) return null;

  const renderContent = () => {
    switch (meetingEvent.status) {
      case 'pending':
        return (
          <LoadingState text="Event is pending processing. Click the 'Process' button." />
        );
      case 'queued': // +++ ADDED
        return <LoadingState text="Event is queued for processing..." />;
      case 'processing':
        return <LoadingState text="Processing event..." />;
      case 'reprocessing':
        return (
          <LoadingState text="Reprocessing event with new transcripts..." />
        );
      case 'failed':
        return <FailedState />;
      case 'processed':
        return meetingEvent.llm_summary ? (
          <ProcessedContent
            data={meetingEvent.llm_summary}
            meetingContext={{
              accessToken,
              eventId: meetingEvent.id,
              isManual: true,
            }}
          />
        ) : (
          <div className="p-8 text-center text-slate-500">
            <p>Processed, but no summary data is available.</p>
          </div>
        );
      default:
        return (
          <div className="p-8 text-center text-slate-500">
            Unknown event status.
          </div>
        );
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-zinc-950">
      {/* Page header */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-zinc-800 bg-zinc-950 px-4 py-3 sm:px-6">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
        >
          <FiArrowLeft size={15} />
          Back
        </button>
        <div className="h-4 w-px bg-zinc-800" />
        <h1 className="min-w-0 truncate text-base font-semibold text-white">
          {meeting.title}
        </h1>
        <span className="ml-auto flex-shrink-0 rounded-full bg-zinc-800 px-2.5 py-0.5 text-[11px] font-medium text-zinc-400">
          {meetingEvent.status}
        </span>
      </div>

      {/* Meeting meta banner */}
      <div className="flex-shrink-0 border-b border-zinc-800/60 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 px-4 py-2.5 sm:px-6">
        <p className="text-sm text-blue-300">
          {formatTranscriptDateTime(meetingEvent.date, meeting.startTime)}
        </p>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        {renderContent()}
      </div>
    </div>,
    modalRoot,
  );
};

export default MeetingEventDetailModal;
